import React, { FC, memo, useEffect, useMemo } from 'react';
import { Box, Grid, makeStyles, Typography } from '@material-ui/core';
import { formatCurrency } from 'src/utils';
import useQuotationProduct from 'src/hooks/quotation-v2/use-quotation-products';
import { slices, useAppDispatch } from 'src/redux';

const useStyles = makeStyles((theme) => ({
  root: {},
  extraInfo: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'end',
    backgroundColor: theme.palette.background.default,
    backgroundSize: '80px 70px',
    backgroundRepeat: 'repeat',
    background:
      'linear-gradient(rgba(255,255,255,.95), rgba(255,255,255,.95)), url("/static/images/avatars/pcworth_logo.png")',
    height: '100%'
  },
  text: {
    fontWeight: 'bold',
    borderTop: 'black solid 2px',
    borderBottom: 'black solid 2px',
    padding: '1em 0'
  },
  price: {
    textAlign: 'right',
    paddingRight: '16px',
    fontSize: '0.9rem'
  }
}));

const { actions: quotationActions } = slices.quotationV2;

interface Props {
  is_srp?: boolean;
  isScreenCapture?: boolean;
}

const Component: FC<Props> = ({ is_srp, isScreenCapture }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { quotationData, quotationParams } = useQuotationProduct();
  const capturingScreen = is_srp && !isScreenCapture;
  const isSrpPricing = quotationParams?.is_srp;

  const grandTotal = useMemo(() => {
    const total =
      quotationData?.reduce(
        (sum, q) => sum + (q?.quantity || 0) * (q?.product?.retail_price || 0),
        0
      ) || 0;
    const srp = total * (isSrpPricing ? 1.12 : 1);
    const promo = total;
    const discounted = srp - promo;
    return {
      SRP: +srp.toFixed(2),
      promo: +promo.toFixed(2),
      discounted: +discounted.toFixed(2)
    };
  }, [isSrpPricing, quotationData]);

  useEffect(() => {
    dispatch(quotationActions.setTotalAmount(grandTotal.promo));
  }, [dispatch, grandTotal.promo]);

  return (
    <Box className={classes.extraInfo}>
      <Grid container alignItems="center" justifyContent="flex-end">
        <Grid item xs={2} sm={2}>
          {is_srp && !isScreenCapture ? (
            <Box className={classes.text}>
              <Typography
                style={{
                  whiteSpace: 'nowrap',
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}
              >
                TOTAL PRICE (SRP)
              </Typography>
              <Typography
                style={{
                  whiteSpace: 'nowrap',
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}
              >
                TOTAL PROMO PRICE
              </Typography>
              <Typography
                style={{
                  color: 'green',
                  whiteSpace: 'nowrap',
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}
              >
                TOTAL DISCOUNTED PRICE
              </Typography>
            </Box>
          ) : (
            <Typography
              className={classes.text}
              style={{
                textAlign: 'center'
              }}
            >
              GRAND TOTAL
            </Typography>
          )}
        </Grid>

        <Grid item xs={2} sm={2}>
          {capturingScreen ? (
            <Box className={classes.text}>
              <Typography className={classes.price}>
                {formatCurrency(grandTotal.SRP || 0)}
              </Typography>
              <Typography className={classes.price}>
                {formatCurrency(grandTotal.promo || 0)}
              </Typography>
              <Typography className={classes.price}>
                {formatCurrency(grandTotal.discounted || 0)}
              </Typography>
            </Box>
          ) : (
            <Typography
              className={classes.text}
              style={{
                textAlign: 'right',
                paddingRight: '16px'
              }}
            >
              {formatCurrency(grandTotal.SRP || 0)}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export const QuotationTotalPrice = memo(Component);
